import React from 'react'
// libs
import { FieldArray } from 'redux-form'
// components
import FieldInfo from '../MainInfo'
import ColorSelect from '../ColorSelect'
import SectionSelect from '../SectionSelect'
import PicklistSettings from './components/PicklistSettings'
import FieldMappingsSettings from '../FieldMappingsSettings'
import AiSuggestedPrompts from 'components/Modals/RiskTemplateSettingsModal/components/Form/components/TemplatePrompts'
import { ReduxInput, ReduxSwitch } from 'components/FormElements'
// views
import { CopyIcon } from 'components/Modals/PropertyDetailsModal/components/Form/view'
// hooks
import { useSnackbar } from 'notistack'
import { useFieldType } from 'hooks/risks'
import { useCurrentUser } from 'hooks/useCurrentUser'
// constants
import { AI_SUPPORTED_FIELD_TYPES } from 'constants/ai'
// types
import { FieldSource, FieldType, IField } from 'types/graphql'

interface Props {
  property: IField
}

const fieldsToExcludeHistory = [
  FieldType.Json,
  FieldType.Table,
  FieldType.Label,
  FieldType.InjuryMasterLinks,
  FieldType.InjuryMasterParentLinks
]
const fieldsToExcludeAiSettings = [
  FieldType.Table,
  FieldType.Label,
  FieldType.Section,
  FieldType.RecordLinks,
  FieldType.DocumentOrLink,
  FieldType.ParentRelatedForms,
  FieldType.InjuryMasterLinks,
  FieldType.InjuryMasterParentLinks
]

const FormFields: React.FC<Props> = ({ property }) => {
  const { enqueueSnackbar } = useSnackbar()

  const {
    permissionsFromSecurityGroups: { aiManageSettings }
  } = useCurrentUser()

  const {
    isText,
    isNumber,
    isLabel,
    isMatrix,
    isSection,
    isPickList,
    isRecordLinks,
    isParentFields,
    isInjuryMasterLinks
  } = useFieldType(property)

  const showHistory = !fieldsToExcludeHistory.includes(property.fieldType)
  const showAiSettings = aiManageSettings && !fieldsToExcludeAiSettings.includes(property.fieldType)
  const showAiPrompts = AI_SUPPORTED_FIELD_TYPES.includes(property.fieldType)
  const showEmails = property.source?.includes(FieldSource.Users)

  const onCopyClick = () => {
    navigator.clipboard.writeText(property.id)

    enqueueSnackbar('Copied!', {
      variant: 'success',
      autoHideDuration: 1000
    })
  }

  return (
    <>
      <ReduxSwitch name='readonly' label='Read Only' reverse />
      <ReduxSwitch name='hidden' label='Hidden' reverse />
      <ReduxSwitch name='includeHistoryInApiResponse' label='Include History in API Response' reverse />
      {showHistory && <ReduxSwitch name='historyEnabled' label='Store History' reverse />}
      {isMatrix && (
        <>
          <ReduxSwitch name='settings.appetiteEnabled' label='Appetite Statements' reverse />
          <ReduxSwitch name='exportHeaders' label='Export Headers' reverse />
        </>
      )}
      {showEmails && <ReduxSwitch name='displayEmail' label='Display Emails' reverse />}
      {isLabel && <ReduxSwitch name='wysiwyg' label='Display as WYSIWYG' reverse />}
      {(isText || isNumber) && <ReduxSwitch name='settings.manualSave' label='Manual Save' reverse />}
      <ReduxSwitch name='hideOnPublic' label='Hide on Public' reverse />
      <ReduxSwitch name='isPublicOnly' label='Public Only' reverse />
      {isText && <ReduxSwitch name='searchable' label='Searchable' reverse />}
      {showAiSettings && (
        <>
          <ReduxSwitch name='includeInAiContext' label='Include in AI Context' reverse />
          <ReduxSwitch name='aiIncludeHistory' label='Include History in AI Context' reverse />
          <ReduxSwitch name='aiAutoPopulate' label='AI Auto-Populate' reverse />
          <ReduxInput name='aiContextDescription' label='AI Context Description' />
          {showAiPrompts && (
            <FieldArray<any> name='aiSuggestedPrompts' component={AiSuggestedPrompts} label='AI Suggested Prompts' />
          )}
        </>
      )}
      <ReduxInput name='fieldId' label='Field ID' disabled endAdornment={<CopyIcon onClick={onCopyClick} />} />
      <FieldInfo property={property} isForm />
      <SectionSelect property={property} />
      {isPickList && <PicklistSettings />}
      {(isSection || isInjuryMasterLinks) && <ColorSelect property={property} />}
      {(isRecordLinks || isParentFields) && <FieldMappingsSettings isMultiple={isParentFields} />}
    </>
  )
}

export default FormFields
